/**
 * DemoLeadsManager — admin panel component to browse and manage
 * "Book a Demo" leads captured from the marketing pages.
 *
 * Reused for both:
 *   • Drive Leads    (source="drive"   → Used Car Dealer leads)
 *   • Pitcrew Leads  (source="pitcrew" → Garage leads)
 *
 * Backend endpoints:
 *   GET    /api/admin/demo-leads?source=…
 *   PATCH  /api/admin/demo-leads/{id}    { status?, notes? }
 *   DELETE /api/admin/demo-leads/{id}
 *
 * Uses the same `admin_token` header pattern as the rest of the admin UI.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Search, Phone, Mail, MapPin, Building2, Trash2, StickyNote,
  CheckCircle2, Clock, PhoneCall, ThumbsUp, XCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { key: 'all', label: 'All', icon: null },
  { key: 'new', label: 'New', icon: Clock, tone: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'contacted', label: 'Contacted', icon: PhoneCall, tone: 'text-amber-600 bg-amber-50 border-amber-200' },
  { key: 'qualified', label: 'Qualified', icon: ThumbsUp, tone: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { key: 'closed', label: 'Closed', icon: CheckCircle2, tone: 'text-zinc-600 bg-zinc-100 border-zinc-200' },
];

const STATUS_META = STATUS_OPTIONS.reduce((acc, s) => {
  if (s.key !== 'all') acc[s.key] = s;
  return acc;
}, {});

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function DemoLeadsManager({ source, title, brand }) {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [saving, setSaving] = useState(null);
  const [notesDraft, setNotesDraft] = useState({}); // id -> string
  const [notesOpen, setNotesOpen] = useState(null); // lead id or null

  const authHeader = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}`,
  }), []);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/api/admin/demo-leads`, { params: { source }, headers: authHeader })
      .then((r) => {
        setLeads(r.data.leads || []);
        setCounts(r.data.counts || {});
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load leads'))
      .finally(() => setLoading(false));
  }, [source, authHeader]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (status !== 'all' && l.status !== status) return false;
      if (!q) return true;
      return [l.name, l.phone, l.email, l.business_name, l.location]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [leads, query, status]);

  const setLeadStatus = async (id, next) => {
    setSaving(id);
    try {
      const r = await axios.patch(`${API}/api/admin/demo-leads/${id}`, { status: next }, { headers: authHeader });
      setLeads((prev) => prev.map((l) => (l.id === id ? r.data.lead : l)));
      // Refresh sidebar counts (cheap: single extra call — keeps them accurate)
      const rr = await axios.get(`${API}/api/admin/demo-leads`, { params: { source }, headers: authHeader });
      setCounts(rr.data.counts || {});
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(null);
    }
  };

  const saveNotes = async (id) => {
    const notes = notesDraft[id] ?? '';
    setSaving(id);
    try {
      const r = await axios.patch(`${API}/api/admin/demo-leads/${id}`, { notes }, { headers: authHeader });
      setLeads((prev) => prev.map((l) => (l.id === id ? r.data.lead : l)));
      setNotesOpen(null);
      toast.success('Notes saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    setSaving(id);
    try {
      await axios.delete(`${API}/api/admin/demo-leads/${id}`, { headers: authHeader });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(null);
    }
  };

  const total = counts.new + counts.contacted + counts.qualified + counts.closed || leads.length;

  return (
    <div className="space-y-4" data-testid={`demo-leads-${source}`}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className={`text-xs uppercase tracking-widest font-bold ${brand === 'orange' ? 'text-orange-600' : 'text-blue-600'}`}>{title}</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 mt-1">
            {total} total {total === 1 ? 'lead' : 'leads'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, email…"
              className="pl-9 pr-3 py-2 rounded-lg bg-white border border-zinc-300 text-sm w-72 focus:border-zinc-900 outline-none"
              data-testid={`demo-leads-${source}-search`}
            />
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-700 hover:border-zinc-500"
            data-testid={`demo-leads-${source}-refresh`}
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Status chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => {
          const active = status === s.key;
          const c = s.key === 'all' ? total : (counts[s.key] ?? 0);
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              data-testid={`demo-leads-${source}-status-${s.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                active
                  ? (brand === 'orange' ? 'bg-orange-500 text-white border-orange-500' : 'bg-blue-600 text-white border-blue-600')
                  : 'bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500'
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />} {s.label} <span className="opacity-70">·  {c}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="min-h-[240px] flex items-center justify-center bg-white border border-zinc-200 rounded-xl">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center bg-white border border-zinc-200 rounded-xl">
          <p className="font-semibold text-zinc-700">No leads match your filters.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-widest text-zinc-500 font-bold">
              <tr>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((l) => {
                const meta = STATUS_META[l.status] || STATUS_META.new;
                return (
                  <React.Fragment key={l.id}>
                    <tr className="align-top hover:bg-zinc-50/60" data-testid={`demo-leads-${source}-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-bold text-zinc-900">{l.name}</div>
                        {l.notes && <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2 max-w-xs">{l.notes}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-zinc-800 font-semibold">
                          <Building2 className="w-3.5 h-3.5 text-zinc-400" /> {l.business_name}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-1">
                          <MapPin className="w-3.5 h-3.5" /> {l.location}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-zinc-800 hover:text-zinc-900 font-medium">
                          <Phone className="w-3.5 h-3.5 text-zinc-400" /> {l.phone}
                        </a>
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mt-1">
                          <Mail className="w-3.5 h-3.5" /> {l.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-600 whitespace-nowrap">{formatDate(l.created_at)}</td>
                      <td className="px-4 py-3">
                        <select
                          value={l.status}
                          onChange={(e) => setLeadStatus(l.id, e.target.value)}
                          disabled={saving === l.id}
                          data-testid={`demo-leads-${source}-status-select-${l.id}`}
                          className={`px-2 py-1 rounded-md text-xs font-semibold border ${meta.tone}`}
                        >
                          {Object.values(STATUS_META).map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              setNotesDraft((d) => ({ ...d, [l.id]: l.notes || '' }));
                              setNotesOpen(notesOpen === l.id ? null : l.id);
                            }}
                            title="Notes"
                            className="p-1.5 rounded-md text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                            data-testid={`demo-leads-${source}-notes-toggle-${l.id}`}
                          >
                            <StickyNote className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeLead(l.id)}
                            title="Delete"
                            className="p-1.5 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`demo-leads-${source}-delete-${l.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {notesOpen === l.id && (
                      <tr className="bg-zinc-50" data-testid={`demo-leads-${source}-notes-editor-${l.id}`}>
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <div className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider mb-1">Internal notes</div>
                              <textarea
                                value={notesDraft[l.id] ?? ''}
                                onChange={(e) => setNotesDraft((d) => ({ ...d, [l.id]: e.target.value }))}
                                rows={3}
                                placeholder="e.g. Called on 24 Feb, follow up next week"
                                className="w-full px-3 py-2 rounded-lg bg-white border border-zinc-300 text-sm focus:border-zinc-900 outline-none"
                              />
                            </div>
                            <button
                              onClick={() => saveNotes(l.id)}
                              disabled={saving === l.id}
                              className={`px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60 ${
                                brand === 'orange' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                              data-testid={`demo-leads-${source}-notes-save-${l.id}`}
                            >
                              {saving === l.id ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              onClick={() => setNotesOpen(null)}
                              className="p-2 rounded-lg text-zinc-500 hover:text-zinc-800"
                              aria-label="Close"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
